import { getUserEmail } from "helpers/local_storage"
import { Route, Router, Switch } from "react-router-dom"
import { history } from "helpers/history"
import { PATH } from "constants/path"

import Login from "pages/auth/Login"
import SignUp from "pages/auth/SignUp"
import ForgetPassword from "pages/auth/ForgetPassword"
import PasswordReset from "pages/auth/PasswordReset"
import PasswordResetSuccess from "pages/auth/PasswordResetSuccess"
import EmailVerification from "pages/auth/EmailVerification"
import ConfirmEmail from "pages/auth/ConfirmEmail"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import {
  API_URL_RECAPTCHA,
  UMAMI_TRACKING_ID,
  UMAMI_URL,
} from "config/environments"
import Main from "pages/projects/Projects"
import PrivateRoute from "components/Router/PrivateRoute"
import PublicRoute from "components/Router/PublicRoute"
import General from "pages/auth/General"
import ForceEncryption from "pages/auth/ForceEncryption"
import NotFoundPage from "pages/layout/NotFoundPage"
import PCBTemplate from "pages/project-component-detail/PCBTemplate"
import BOMTemplate from "pages/project-component-detail/BOMTemplate"
import OtherTemplate from "pages/project-component-detail/OtherTemplate"
import MechTemplate from "pages/project-component-detail/MechTemplate"
import Conversations from "pages/conversations/Conversations"
import InviteSentSignUp from "pages/auth/InviteSentSignUp"
import InviteePCBComponent from "pages/conversations/organisms/invitees-component/InviteePCBComponent"
import InviteeBOMComponent from "pages/conversations/organisms/invitees-component/InviteeBOMComponent"
import InviteeOtherComponent from "pages/conversations/organisms/invitees-component/InviteeOtherComponent"
import InviteeMechComponent from "pages/conversations/organisms/invitees-component/InviteeMechComponent"
import TermsAndPolicyPage from "pages/terms-policy/TermsAndPolicyPage"
import ProjectSearchPage from "pages/projects/ProjectSearchPage"
import ProjectComponent from "pages/project-component/ProjectComponent"
import BuildOverview from "pages/build-overview/BuildOverView"
import { setupInterceptors } from "services/axios"
import { useEffect } from "react"
import { debounce } from "lodash"
import StorageUsagePage from "pages/storage-usage/StorageUsagePage"
import { initDB } from "services/indexedDB"
import ProjectSync from "pages/projects/ProjectSync"
import { AddressBookPage } from "pages/address-book/AddressBookPage"
import { ActionLogPage } from "pages/action-log/ActionLogPage"
import { AccessKeyPage } from "pages/access-key/AccessKeyPage"
import { UpdatePhoneNumberAndSecurityCode } from "pages/auth/UpdatePhoneNumberAndSecurityCode"
import UserPage from "pages/user/UserPage"
import { SyncCommitComponentPage } from "pages/sync-commit-component/SyncCommitComponentPage"
import AddOrganizationPage from "pages/organization/AddOrganizationPage"
import { SyncToOrganizationPage } from "pages/projects/SyncToOrganizationPage"
import { ShareKeyOrganizationPage } from "pages/project-share-organization/ShareKeyOrganizationPage"
import { GroupDetailPage } from "pages/groups/GroupDetailPage"
import { AssignToGroupPage } from "pages/projects/AssignToGroupPage"
import { WorkspaceSettingPage } from "pages/organization/WorkspaceSettingPage"
import { AssignmentPage } from "pages/user/AssignmentPage"
import {
  getSavedWsCode,
  getWsCodeFromUrl,
  isRouteNotAllowedWithWsCode,
} from "helpers/wsCode.helper"
// setup interceptor
setupInterceptors()

// Helper function để thêm prefix :wsCode vào path nếu path không nằm trong routesWithoutWsCode
const addWsCodePrefix = (path: string): string => {
  if (isRouteNotAllowedWithWsCode(path)) {
    return path
  }
  // Thêm prefix :wsCode vào trước path
  // Xử lý đặc biệt cho path "/"
  if (path === "/") {
    return "/:wsCode"
  }
  return `/:wsCode${path}`
}
declare global {
  interface Window {
    umami?: {
      track: (event: string, payload?: Record<string, any>) => void
      identify?: (data: Record<string, string | undefined>) => void
    }
    /** Umami tracker reads this global before sending (script-dependent). */
    umamiConfig?: {
      beforeSend?: (
        type: string,
        payload: { url?: string; [key: string]: unknown }
      ) => typeof payload | false
    }
    traceliumUmamiBeforeSend?: (
      type: string,
      payload: { url?: string; [key: string]: unknown }
    ) => typeof payload | false
    Quill: any
  }
}
export const AppRouter = () => {
  useEffect(() => {
    const resize = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty("--vhi", `${vh}px`)
    }
    resize()
    window.umamiConfig = {
      beforeSend: (type, payload) => {
        if (payload?.url) {
          try {
            const path = new URL(payload.url as string, window.location.origin)
              .pathname
            const segments = path.split("/").filter(Boolean)
            const wsCodePattern = /^[A-Z0-9]+$/
            if (segments[0] && wsCodePattern.test(segments[0])) {
              const wsCode = segments[0]
              const pageType = segments[1] || ""
              ;(payload as { url?: string }).url = `/${wsCode}/${pageType}`
            }
          } catch {
            /* ignore malformed URL */
          }
        }
        const email = getUserEmail()
        if (email) {
          ;(payload as Record<string, unknown>).distinct_id = email
        }
        return payload
      },
    }

    window.traceliumUmamiBeforeSend = (type, payload) => {
      const cfg = window.umamiConfig?.beforeSend
      if (cfg) return cfg(type, payload)
      return payload
    }

    const umamiScript = document.createElement("script")
    umamiScript.async = true
    umamiScript.src = UMAMI_URL // Replace with your Umami instance URL
    umamiScript.setAttribute("data-website-id", UMAMI_TRACKING_ID) // Replace with your Tracking ID
    umamiScript.setAttribute("data-before-send", "traceliumUmamiBeforeSend")
    const wsCode = getWsCodeFromUrl() || getSavedWsCode()
    if (wsCode) {
      umamiScript.setAttribute("data-tag", wsCode)
    }
    document.body.appendChild(umamiScript)
    const debounceResize = debounce(resize, 50)
    window.addEventListener("resize", debounceResize)

    return () => {
      window.removeEventListener("resize", debounceResize)
      document.body.removeChild(umamiScript)
    }
  }, [])

  useEffect(() => {
    initDB()
      .then((res) => {
        // console.log(res)
      })
      .catch((error) => {
        console.log(error)
      })
  }, [])

  return (
    <Router history={history}>
      <Switch>
        <PublicRoute
          exact
          path={PATH.login}
          component={
            <GoogleReCaptchaProvider reCaptchaKey={API_URL_RECAPTCHA}>
              <Login />
            </GoogleReCaptchaProvider>
          }
        />
        <PublicRoute
          exact
          path={PATH.signUp}
          component={
            <GoogleReCaptchaProvider reCaptchaKey={API_URL_RECAPTCHA}>
              <SignUp />
            </GoogleReCaptchaProvider>
          }
        />
        <PublicRoute
          exact
          path={PATH.forget}
          component={
            <GoogleReCaptchaProvider reCaptchaKey={API_URL_RECAPTCHA}>
              <ForgetPassword />
            </GoogleReCaptchaProvider>
          }
        />
        <PublicRoute
          path={PATH.reset}
          component={
            <GoogleReCaptchaProvider reCaptchaKey={API_URL_RECAPTCHA}>
              <PasswordReset />
            </GoogleReCaptchaProvider>
          }
        />
        <PublicRoute
          exact
          path={PATH.resetSuccess}
          component={<PasswordResetSuccess />}
        />
        <Route
          exact
          path={PATH.termsAndServices}
          render={() => <TermsAndPolicyPage />}
        />

        <Route
          exact
          path={PATH.policies}
          render={() => <TermsAndPolicyPage />}
        />
        <PublicRoute
          path={PATH.emailVerification}
          component={<EmailVerification />}
        />
        <PublicRoute
          path={PATH.signingUpMessage}
          component={<EmailVerification />}
        />
        <PublicRoute path={PATH.confirmEmail} component={<ConfirmEmail />} />
        <PublicRoute
          path={PATH.inviteSentSignUp}
          component={
            <GoogleReCaptchaProvider reCaptchaKey={API_URL_RECAPTCHA}>
              <InviteSentSignUp />
            </GoogleReCaptchaProvider>
          }
        />

        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.general)}
          component={<General />}
        />
        <PrivateRoute exact path={PATH.general} component={<General />} />

        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.updatePhoneNumberAndSecurityCode)}
          component={<UpdatePhoneNumberAndSecurityCode />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.enableEncryption)}
          component={<ForceEncryption />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.StorageUsage)}
          component={<StorageUsagePage />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.projects)}
          component={<Main />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.generalSearch)}
          component={<ProjectSearchPage />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.buildsOverview)}
          component={<BuildOverview />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.buildOverview)}
          component={<BuildOverview />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.defaultBuildOverview)}
          component={<BuildOverview />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.conversations)}
          component={<Conversations />}
        />
        <PrivateRoute
          path={PATH.inviteesPCB}
          component={<InviteePCBComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesBuildPCB}
          component={<InviteePCBComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesBOM}
          component={<InviteeBOMComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesBuildBOM}
          component={<InviteeBOMComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesMechComponent}
          component={<InviteeMechComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesBuildMechComponent}
          component={<InviteeMechComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesOther}
          component={<InviteeOtherComponent />}
        />
        <PrivateRoute
          path={PATH.inviteesBuildOther}
          component={<InviteeOtherComponent />}
        />
        {/* <PrivateRoute exact path={PATH.settings} component={<General />} /> */}
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.projectComponent)}
          component={<ProjectComponent />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.projectSensitiveData)}
          component={<ProjectSync />}
        />

        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.projectComponentPCB)}
          component={<PCBTemplate />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.projectComponentBOM)}
          component={<BOMTemplate />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.projectComponentMech)}
          component={<MechTemplate />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.projectComponentOther)}
          component={<OtherTemplate />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.folder)}
          component={<ProjectComponent />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.addressBook)}
          component={<AddressBookPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.actionLog)}
          component={<ActionLogPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.accessKey)}
          component={<AccessKeyPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.userGroups)}
          component={<UserPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.syncCommitComponent)}
          component={<SyncCommitComponentPage />}
        />
        <PrivateRoute
          exact
          path={PATH.organizationAdd}
          component={<AddOrganizationPage />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.organizationEdit)}
          component={<AddOrganizationPage />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.syncToOrganization)}
          component={<SyncToOrganizationPage />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.shareKeyOrganization)}
          component={<ShareKeyOrganizationPage />}
        />
        {/* <PrivateRoute exact path={PATH.groups} component={<GroupsPage />} /> */}
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.groupDetail)}
          component={<GroupDetailPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.assignToGroupPage)}
          component={<AssignToGroupPage />}
        />
        <PrivateRoute
          exact
          path={addWsCodePrefix(PATH.workspaceSetting)}
          component={<WorkspaceSettingPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix(PATH.assignment)}
          component={<AssignmentPage />}
        />
        <PrivateRoute
          path={addWsCodePrefix("*")}
          component={<NotFoundPage />}
        />
      </Switch>
    </Router>
  )
}
